// Lightweight i18n for the /app chrome. English strings are the keys, so
// `t("Settings")` falls back to the key itself for English (or any missing
// translation); a regional language (es-MX, fr-CA) falls back to its base
// dictionary. Everything these dictionaries do not cover is translated live
// by utils/liveTranslate.js. Generated documents intentionally stay English,
// only the UI translates. setLanguage() notifies subscribers so useLanguage()
// re-renders live when the language changes.
import { useEffect, useReducer } from "react";

const DICTS = {
  es: {
    "Pay Stubs": "Talones de pago",
    "Canadian Stubs": "Talones canadienses",
    "UK Payslips": "Nóminas del Reino Unido",
    "Tax Forms": "Formularios fiscales",
    "Legal Forms": "Formularios legales",
    "Business Forms": "Formularios de negocios",
    "Resumes": "Currículums",
    "Settings": "Configuración",
    "Terms of Service": "Términos del servicio",
    "Privacy Policy": "Política de privacidad",
    "Subscriber": "Suscriptor",
    "Free": "Gratis",
    "User": "Usuario",
    "Notifications": "Notificaciones",
    "Clear all": "Borrar todo",
    "No notifications yet": "Aún no hay notificaciones",
    "View": "Ver",
    "Create Pay Stub": "Crear talón de pago",
    "Create Canadian Paystub": "Crear talón canadiense",
    "Create UK Payslip": "Crear nómina del Reino Unido",
    "Create Offer Letter": "Crear carta de oferta",
    "Create Accounting Mockup": "Crear simulación contable",
    "Build AI Resume": "Crear currículum con IA",
    "Build Tax Forms": "Crear formularios fiscales",
    "Build Legal Forms": "Crear formularios legales",
    "Build Business Forms": "Crear formularios de negocios",
    "Cancel": "Cancelar",
    "Status": "Estado",
    "App Status": "Estado de la app",
    "Normal": "Normal",
    "Degraded": "Degradado",
    "Down": "Caído",
    "Current version": "Versión actual",
    "Latest": "Última",
    "What's New": "Novedades",
    "Known Issues": "Problemas conocidos",
    "Appearance": "Apariencia",
    "Language": "Idioma",
    "Dark theme": "Tema oscuro",
    "Show support chat": "Mostrar chat de soporte",
    "Allow push notifications": "Permitir notificaciones push",
    "Notifications are blocked for MintSlip in your phone's Settings. Allow them there first.": "Las notificaciones están bloqueadas para MintSlip en los ajustes de tu teléfono. Permítelas allí primero.",
    "Support": "Soporte",
    "Tutorials": "Tutoriales",
    "Feature Request": "Sugerir función",
    "Report a Problem": "Reportar un problema",
    "About": "Acerca de",
    "Search": "Buscar",
    "No results found": "No se encontraron resultados",
    "No tutorials available yet.": "Aún no hay tutoriales disponibles.",
    "No videos in this category.": "No hay videos en esta categoría.",
    "Playlists": "Listas de reproducción",
    "video": "video",
    "videos": "videos",
    "Your completed downloads will appear here": "Tus descargas completadas aparecerán aquí",
    "Generating...": "Generando...",
    "Ready to download": "Listo para descargar",
    "Generation failed": "La generación falló",
    "CREATE": "CREAR",
    "Navigate": "Navegar",
    "Home": "Inicio",
    "What would you like to create today?": "¿Qué te gustaría crear hoy?",
    "Professional documents in minutes.": "Documentos profesionales en minutos.",
    "Create a Pay Stub": "Crea un talón de pago",
    "Create a W-2": "Crea un W-2",
    "Build Your AI Resume": "Crea tu currículum con IA",
    "Create a Commercial Lease": "Crea un contrato de arrendamiento",
    "Create an Offer Letter": "Crea una carta de oferta",
    "Generate a 1099": "Genera un 1099",
    "Create a Power of Attorney": "Crea un poder notarial",
  },
  fr: {
    "Pay Stubs": "Bulletins de paie",
    "Canadian Stubs": "Bulletins canadiens",
    "UK Payslips": "Fiches de paie britanniques",
    "Tax Forms": "Formulaires fiscaux",
    "Legal Forms": "Formulaires juridiques",
    "Business Forms": "Formulaires d'affaires",
    "Resumes": "CV",
    "Settings": "Paramètres",
    "Terms of Service": "Conditions d'utilisation",
    "Privacy Policy": "Politique de confidentialité",
    "Subscriber": "Abonné",
    "Free": "Gratuit",
    "User": "Utilisateur",
    "Notifications": "Notifications",
    "Clear all": "Tout effacer",
    "No notifications yet": "Aucune notification pour l'instant",
    "View": "Voir",
    "Create Pay Stub": "Créer un bulletin de paie",
    "Create Canadian Paystub": "Créer un bulletin canadien",
    "Create UK Payslip": "Créer une fiche de paie britannique",
    "Create Offer Letter": "Créer une lettre d'offre",
    "Create Accounting Mockup": "Créer une maquette comptable",
    "Build AI Resume": "Créer un CV avec l'IA",
    "Build Tax Forms": "Créer des formulaires fiscaux",
    "Build Legal Forms": "Créer des formulaires juridiques",
    "Build Business Forms": "Créer des formulaires d'affaires",
    "Cancel": "Annuler",
    "Status": "État",
    "App Status": "État de l'application",
    "Normal": "Normal",
    "Degraded": "Dégradé",
    "Down": "En panne",
    "Current version": "Version actuelle",
    "Latest": "Dernière",
    "What's New": "Nouveautés",
    "Known Issues": "Problèmes connus",
    "Appearance": "Apparence",
    "Language": "Langue",
    "Dark theme": "Thème sombre",
    "Show support chat": "Afficher le chat d'assistance",
    "Allow push notifications": "Autoriser les notifications push",
    "Notifications are blocked for MintSlip in your phone's Settings. Allow them there first.": "Les notifications sont bloquées pour MintSlip dans les réglages de votre téléphone. Autorisez-les d'abord.",
    "Support": "Assistance",
    "Tutorials": "Tutoriels",
    "Feature Request": "Suggérer une fonctionnalité",
    "Report a Problem": "Signaler un problème",
    "About": "À propos",
    "Search": "Rechercher",
    "No results found": "Aucun résultat trouvé",
    "No tutorials available yet.": "Aucun tutoriel disponible pour l'instant.",
    "No videos in this category.": "Aucune vidéo dans cette catégorie.",
    "Playlists": "Playlists",
    "video": "vidéo",
    "videos": "vidéos",
    "Your completed downloads will appear here": "Vos téléchargements terminés apparaîtront ici",
    "Generating...": "Génération...",
    "Ready to download": "Prêt à télécharger",
    "Generation failed": "Échec de la génération",
    "CREATE": "CRÉER",
    "Navigate": "Naviguer",
    "Home": "Accueil",
    "What would you like to create today?": "Que souhaitez-vous créer aujourd'hui ?",
    "Professional documents in minutes.": "Des documents professionnels en quelques minutes.",
    "Create a Pay Stub": "Créez un bulletin de paie",
    "Create a W-2": "Créez un W-2",
    "Build Your AI Resume": "Créez votre CV avec l'IA",
    "Create a Commercial Lease": "Créez un bail commercial",
    "Create an Offer Letter": "Créez une lettre d'offre",
    "Generate a 1099": "Générez un 1099",
    "Create a Power of Attorney": "Créez une procuration",
  },
};

// Every language the app offers: its code, its native name and the flag it
// wears in the settings picker (public/assets/images/<flag>_flag.svg).
export const LANGUAGES = [
  { code: "en", name: "English", flag: "us" },
  { code: "es", name: "Español", flag: "es" },
  { code: "es-MX", name: "Español", flag: "mx" },
  { code: "fr", name: "Français", flag: "fr" },
  { code: "fr-CA", name: "Français", flag: "ca" },
  { code: "it", name: "Italiano", flag: "it" },
  { code: "de", name: "Deutsch", flag: "de" },
  { code: "nl", name: "Nederlands", flag: "nl" },
];

let current = localStorage.getItem("appLanguage") || "en";
const listeners = new Set();

export function getLanguage() {
  return current;
}

export function setLanguage(lang) {
  current = lang;
  localStorage.setItem("appLanguage", lang);
  listeners.forEach((fn) => { try { fn(lang); } catch (e) {} });
}

export function t(text) {
  const d = DICTS[current] || DICTS[current.split("-")[0]];
  return d?.[text] ?? text;
}

// Subscribe outside React (the live translator); returns the unsubscribe.
export function subscribeLanguage(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Subscribe a component to language changes; returns the current language.
export function useLanguage() {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => listeners.delete(force);
  }, []);
  return current;
}
